import Space from 'antd/lib/space';
import React from 'react';
import { AppComponentProps, QuickStartCISetupStep, QuickStartCISetupViewProps } from '../../../typescriptHelpers';
import { InfoTip, SecureTip, setupModuleStyle } from '../../setup/shared';
import { KeyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectAppStoreType, selectVCSIntegration } from '../../../selectors/selectors';
import { QuestionsInfoSection } from '../QuestionsInfoSection';
import { SigningSecretsForm } from '../SigningSecretsForm';
import Link from 'antd/lib/typography/Link';
import { AppStoreType } from '../../../types';

const SetupViewComponent: React.FC<QuickStartCISetupViewProps> = ({ appId, orgId }) => {
    return (
        <div
            style={{
                ...setupModuleStyle,
            }}
        >
            <SigningSecretsForm appId={appId} orgId={orgId} />
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = ({ appId }) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    return (
        <Space direction="vertical">
            <InfoTip>
                For GitHub Actions, we’ll upload signing secrets to GitHub’s{' '}
                <Link href="https://docs.github.com/en/actions/security-guides/encrypted-secrets" target="_self">
                    encrypted secrets
                </Link>{' '}
                in your selected repo, so they can be securely accessed by your workflow.
            </InfoTip>
            <InfoTip>
                {appStoreType == AppStoreType.Apple ?
                    <>
                        You can create a signing certificate and private key pair from the Apple Developer portal. For
                        more information, visit the{' '}
                        <Link
                            href="https://developer.apple.com/help/account/create-certificates/certificates-overview"
                            target="_self"
                        >
                            Apple Certificates Overview docs
                        </Link>
                        .
                    </>
                :   <>
                        For details on how to create a signing key or upload key, visit Google’s{' '}
                        <Link href="https://developer.android.com/studio/publish/app-signing" target="_self">
                            App Signing documentation
                        </Link>
                        .
                    </>
                }
            </InfoTip>
            <SecureTip>
                We take security seriously.{' '}
                <Link href="https://www.runway.team/security" target="_blank">
                    Read more
                </Link>{' '}
                about how we handle sensitive information.
                <br />
                <br />
                Your signing keys are uploaded directly to GitHub’s encrypted repo secrets. They are never stored
                anywhere on Runway’s servers.
            </SecureTip>
            <QuestionsInfoSection />
        </Space>
    );
};

export const UploadSecrets: QuickStartCISetupStep = {
    icon: () => KeyOutlined,
    headerTitle: 'Securely upload your build signing secrets to GitHub’s encrypted repo secrets.',
    SetupViewComponent,
    InfoViewComponent,
    isCompleteSelector: (state, { appId }) => {
        const vcsIntegration = selectVCSIntegration(state, appId);
        return !!vcsIntegration;
    },
};
